/**
 * Easter labels — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterLabelsPack: ToilePack = {
    slug: 'easter-labels',
    category: 'season',
    name: 'Easter labels',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'jar', label: 'Jar labels', subject: 'A sheet of round labels each with a blossom border and the rim lettered exactly "Happy Easter" around a blank centre, dashed cut lines, in a grid'},
        {key: 'names', label: 'Name labels', subject: 'A sheet of egg-shaped labels each with a patterned band and a blank ruled centre for the name, a thin border, the corner lettered small "Easter"'},
        {key: 'basket', label: 'Basket labels', subject: 'A sheet of rectangular labels each lettered "Happy Easter" small at the foot with a blank centre, a carrot motif'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for the drinks table instead', 'as a circle sticker sheet', 'with a bunny die-cut shape'],
    directionHint: 'try: a circle sticker shape instead',
    seo: {
        title: 'Easter Labels: Jar, Name & Basket Designs',
        metaDescription: "Nine Easter labels — jar labels, name labels, and basket labels, blank centers included — in pastel, vintage, and kids' styles, print-ready.",
        h1: 'Easter labels: jar, name, and basket designs',
        tagline: 'Nine sheets, three label types, three styles each — blank centers, cut lines drawn.',
        intro: "Three Easter label sheets — round jar labels, egg-shaped name labels, and basket labels — each with a blank center left bare for a pen, in pastel, vintage, and kids' cartoon styles. Ask for a different shape, a bigger blank area, or a family-name version, and the same label grammar carries it.",
        useCases: [
            'Easter treat jars',
            'place settings at an Easter meal',
            'egg-hunt basket tags',
            'classroom party favors',
        ],
        faq: [
            {q: 'Are the centers pre-printed with a name, or blank?', a: 'Blank — every center is left bare in the image so you can write a name or message by hand after printing.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single square or vertical page depending on the label type, close to standard label-sheet proportions.'},
            {q: 'Can I get a different shape or bigger blank area?', a: 'Yes — the direction field takes any variation, including a different shape or a larger blank center.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
